import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { mailReturnMessage } from '@cerebro-diem/core';
import type { MailConnection } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import {
  checkMailbox,
  connectMailbox,
  disconnectMailbox,
  foldersSummary,
  mailConnections,
  mailKeys,
  startOutlookMailboxConnect,
  syncMailbox,
  testMailbox,
  type MailboxInput,
} from '../../services/mail';
import { compactInput, dangerButton, outlineButton, primaryButton } from '../../components/styles';

/**
 * Connect the user's own mailboxes: any IMAP mailbox with its password, or
 * an Outlook.com mailbox through Microsoft's sign-in; check, reconnect and
 * disconnect each. Cerebro only reads mail.
 */
export default function MailSection() {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const returned = mailReturnMessage(params.get('mail'));
  const connections = useQuery({ queryKey: mailKeys.connections, queryFn: mailConnections });
  const refresh = () => queryClient.invalidateQueries({ queryKey: mailKeys.connections });

  const [result, setResult] = useState<string | null>(null);
  const test = useMutation({ mutationFn: testMailbox, onSuccess: (folders) => setResult(foldersSummary(folders)) });
  const connect = useMutation({
    mutationFn: connectMailbox,
    onSuccess: ({ folders }) => {
      setResult(foldersSummary(folders));
      refresh();
    },
  });
  const check = useMutation({
    mutationFn: checkMailbox,
    onSuccess: (folders) => setResult(foldersSummary(folders)),
    onSettled: refresh,
  });
  const sync = useMutation({
    mutationFn: syncMailbox,
    onSuccess: ({ messages, backlog }) =>
      setResult(`Read ${messages} new message${messages === 1 ? '' : 's'}.${backlog ? ' More is waiting; it is read next.' : ''}`),
    onSettled: refresh,
  });
  const disconnect = useMutation({ mutationFn: disconnectMailbox, onSuccess: refresh });
  const outlook = useMutation({ mutationFn: startOutlookMailboxConnect });

  const busy = test.isPending || connect.isPending || check.isPending || sync.isPending || disconnect.isPending || outlook.isPending;
  const failure = [test, connect, check, sync, disconnect, outlook].find((m) => m.isError)?.error;

  return (
    <section className="mb-8" aria-labelledby="mail-heading">
      <h2 id="mail-heading" className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
        <span className="text-xl">✉️</span>
        Mailboxes
      </h2>
      <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6 space-y-4">
        {returned && (
          <p role="status" className={`text-sm ${returned.ok ? 'text-on-surface' : 'text-error'}`}>{returned.message}</p>
        )}
        {connections.isError && <p className="text-sm text-error">Could not load your mailboxes: {errorMessage(connections.error)}</p>}
        {connections.isPending && <p className="text-sm text-on-surface-variant">Loading…</p>}

        {connections.data && connections.data.length > 0 && (
          <ul className="divide-y divide-outline/20">
            {connections.data.map((box) => (
              <MailboxRow
                key={box.id}
                box={box}
                busy={busy}
                onCheck={() => check.mutate(box.id)}
                onSync={() => sync.mutate(box.id)}
                onDisconnect={() => disconnect.mutate(box.id)}
                onReconnect={box.auth === 'microsoft' ? () => outlook.mutate() : undefined}
              />
            ))}
          </ul>
        )}

        <p className="text-on-surface-variant">
          Connect the mailboxes you want Cerebro to read. Cerebro reads each mailbox's Inbox, Junk and Sent folders every five minutes, starting
          two weeks back; it never moves, flags or deletes anything unless you set a policy that says so. Each password is kept encrypted on the
          server and is never shown again.
        </p>

        <div>
          <button type="button" onClick={() => outlook.mutate()} disabled={busy} className={outlineButton}>
            {outlook.isPending ? 'Opening Microsoft…' : 'Connect an Outlook.com mailbox'}
          </button>
          <p className="text-xs text-on-surface-variant mt-1">Outlook.com takes no passwords from apps; you sign in with Microsoft instead.</p>
        </div>

        <MailboxForm
          busy={busy}
          onTest={(input) => {
            setResult(null);
            test.mutate(input);
          }}
          onConnect={(input) => {
            setResult(null);
            connect.mutate(input);
          }}
        />

        {result && <p role="status" className="text-sm text-on-surface">{result}</p>}
        {failure !== undefined && <p role="alert" className="text-sm text-error">{errorMessage(failure)}</p>}
      </div>
    </section>
  );
}

function MailboxRow({
  box,
  busy,
  onCheck,
  onSync,
  onDisconnect,
  onReconnect,
}: {
  box: MailConnection;
  busy: boolean;
  onCheck: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  onReconnect?: () => void;
}) {
  return (
    <li className="py-3 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium text-on-surface break-words">{box.username}</p>
        <p className="text-sm text-on-surface-variant">
          {box.host}
          {box.auth === 'microsoft' ? ', Microsoft sign-in' : ''}
          {box.last_checked_at ? ` · signed in ${new Date(box.last_checked_at).toLocaleString()}` : ''}
        </p>
        <p className="text-sm text-on-surface-variant">
          {box.last_sync_at ? `Read ${new Date(box.last_sync_at).toLocaleString()}` : 'Not read yet'}
          {box.sync_backlog ? ' · more to read' : ''}
        </p>
        {box.needs_reconnect && (
          <p role="status" className="text-sm text-error">
            {box.auth === 'microsoft'
              ? 'Microsoft no longer accepts the saved sign-in. Connect this mailbox again.'
              : 'The mail server no longer accepts the saved password. Enter it again below to reconnect.'}
          </p>
        )}
        {!box.needs_reconnect && box.last_error && <p className="text-sm text-error">Last problem: {box.last_error}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {box.needs_reconnect && onReconnect && (
          <button type="button" onClick={onReconnect} disabled={busy} className={outlineButton}>Connect again</button>
        )}
        <button type="button" onClick={onSync} disabled={busy || box.needs_reconnect} className={outlineButton}>Read now</button>
        <button type="button" onClick={onCheck} disabled={busy || box.needs_reconnect} className={outlineButton}>Check</button>
        <button type="button" onClick={onDisconnect} disabled={busy} className={dangerButton}>Disconnect</button>
      </div>
    </li>
  );
}

/** An IMAP server, user name and password; Test signs in without keeping anything, Connect keeps the mailbox. */
function MailboxForm({
  busy,
  onTest,
  onConnect,
}: {
  busy: boolean;
  onTest: (input: MailboxInput) => void;
  onConnect: (input: MailboxInput) => void;
}) {
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const input = () => ({ host, username, password });
  const complete = host.trim() !== '' && username.trim() !== '' && password !== '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onConnect(input());
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <h3 className="font-medium">Connect a mailbox with its password</h3>
      <label className="block text-sm">
        Mail server
        <input type="text" required value={host} onChange={(e) => setHost(e.target.value)} placeholder="imap.example.com" autoComplete="off"
          className={compactInput} />
      </label>
      <label className="block text-sm">
        User name
        <input type="text" required value={username} onChange={(e) => setUsername(e.target.value)} placeholder="you@example.com"
          autoComplete="username" className={compactInput} />
      </label>
      <label className="block text-sm">
        Password
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
          className={compactInput} />
      </label>
      <p className="text-xs text-on-surface-variant">
        The server is reached over TLS on port 993. Gmail takes an app password here. Connecting a mailbox again replaces its password.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onTest(input())} disabled={busy || !complete} className={outlineButton}>Test</button>
        <button type="submit" disabled={busy || !complete} className={primaryButton}>Connect mailbox</button>
      </div>
    </form>
  );
}
